import React from 'react';

const PlatformInfographic: React.FC = () => {
  const modules = [
    { name: 'CRM', icon: '👥', position: 'top-0 left-1/4' },
    { name: 'Projects', icon: '📋', position: 'top-0 right-1/4' },
    { name: 'Email', icon: '📧', position: 'top-1/4 left-0' },
    { name: 'Social', icon: '📱', position: 'top-1/4 right-0' },
    { name: 'Finance', icon: '💰', position: 'bottom-1/4 left-0' },
    { name: 'HR', icon: '👔', position: 'bottom-1/4 right-0' },
    { name: 'SEO', icon: '🔍', position: 'bottom-0 left-1/4' },
    { name: 'Brand', icon: '🎨', position: 'bottom-0 right-1/4' },
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-blue-50 via-white to-purple-50 relative overflow-hidden">
      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 mb-4 text-sm font-semibold text-indigo-600 bg-indigo-50 rounded-full">
            One Interconnected Platform
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6">
            Not Just Tools —
            <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
              A Complete Business Operating System
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Every module shares data, learns from your business, and works together seamlessly
          </p>
        </div>

        {/* Infographic */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className="relative h-96 md:h-[500px]">
            {/* Central AI Brain */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20">
              <div className="relative">
                {/* Pulsing rings */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 animate-ping opacity-20"></div>
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 animate-pulse opacity-30"></div>
                
                {/* Main brain */}
                <div className="relative w-32 h-32 md:w-40 md:h-40 rounded-full bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 flex flex-col items-center justify-center shadow-2xl border-4 border-white">
                  <span className="text-4xl md:text-5xl mb-2">🧠</span>
                  <span className="text-white font-bold text-xs md:text-sm text-center">AI Brain</span>
                  <span className="text-blue-100 text-xs text-center">Learns & Connects</span>
                </div>
              </div>
            </div>

            {/* Orbiting Modules */}
            {modules.map((module, index) => (
              <div
                key={module.name}
                className={`absolute ${module.position} z-10 animate-float`}
                style={{
                  animationDelay: `${index * 0.3}s`,
                }}
              >
                {/* Connection Line to Center */}
                <svg className="absolute top-1/2 left-1/2 w-full h-full pointer-events-none" style={{ zIndex: -1 }}>
                  <line
                    x1="50%"
                    y1="50%"
                    x2="50%"
                    y2="50%"
                    stroke="url(#gradient)"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                    className="animate-pulse"
                  />
                  <defs>
                    <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.3" />
                      <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0.3" />
                    </linearGradient>
                  </defs>
                </svg>

                {/* Module Card */}
                <div className="group relative">
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-xl bg-white shadow-lg hover:shadow-2xl transition-all duration-300 flex flex-col items-center justify-center border-2 border-blue-100 hover:border-blue-300 hover:scale-110 cursor-pointer">
                    <span className="text-2xl md:text-3xl mb-1">{module.icon}</span>
                    <span className="text-xs font-bold text-gray-700">{module.name}</span>
                  </div>
                  
                  {/* Data flow animation */}
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500 to-purple-500 opacity-0 group-hover:opacity-10 transition-opacity"></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {[
            {
              icon: '🔗',
              title: 'Fully Interconnected',
              description: 'Data flows automatically between modules — no manual exports or imports',
            },
            {
              icon: '🤖',
              title: 'AI-Powered Intelligence',
              description: 'Central AI learns your business and provides insights across all tools',
            },
            {
              icon: '⚡',
              title: 'Instant Automation',
              description: 'Set up workflows once and let the platform handle repetitive tasks',
            },
          ].map((benefit) => (
            <div
              key={benefit.title}
              className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border-2 border-gray-100 hover:border-blue-200"
            >
              <div className="text-4xl mb-3">{benefit.icon}</div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h3>
              <p className="text-gray-600 text-sm">{benefit.description}</p>
            </div>
          ))}
        </div>

        {/* Comparison */}
        <div className="mt-16 max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Old Way */}
            <div className="bg-red-50 rounded-2xl p-8 border-2 border-red-200">
              <div className="text-center mb-4">
                <span className="inline-block px-4 py-2 bg-red-600 text-white font-bold rounded-full text-sm">
                  ❌ The Old Way
                </span>
              </div>
              <ul className="space-y-3">
                {[
                  'Buy 10+ separate tools',
                  'Manual data entry & exports',
                  'Tools don\'t talk to each other',
                  'Pay £200+ per month',
                  'Context switching kills productivity',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-gray-700">
                    <span className="text-red-500 font-bold">✗</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* New Way */}
            <div className="bg-green-50 rounded-2xl p-8 border-2 border-green-200">
              <div className="text-center mb-4">
                <span className="inline-block px-4 py-2 bg-green-600 text-white font-bold rounded-full text-sm">
                  ✅ The Explanatum Way
                </span>
              </div>
              <ul className="space-y-3">
                {[
                  'One platform, 23 AI modules',
                  'Data syncs automatically',
                  'All tools work together',
                  'Pay only £19 per month',
                  'Stay focused, work faster',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-gray-700">
                    <span className="text-green-500 font-bold">✓</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default PlatformInfographic;
